import {createSlice, PayloadAction} from '@reduxjs/toolkit';
/**
 *
 */
export type ResponsibleEmployee = {
  id: number;
  emp_id: string;
  name: string;
};

export type StoryProject = {id: number; proj_id: string; name: string};

export type Follower = {id: number; emp_id: string; name: string};

export type UserStory = {
  id: number;
  us_id: string;
  title: string;
  responsible: ResponsibleEmployee;
  priority: number;
  status: {id: number; name: string};
  created_at: string;
  due_date: string;
  updated_at: string;
  project: StoryProject;
  followers: Follower[];
  is_validated: boolean;
  description?: string;
  tags?: string[];
  is_active?: boolean;
};

export type UserStoryStateType = {
  isLoading: boolean;
  errorUserStoryState: boolean | string;
  successUserStoryState: boolean | string;
  totalBacklogPages: number;
  currentBacklogPage: number;
  backlogsList: UserStory[];
  nextBacklogPage: number | null;
  selectedStoryData: {
    id: number | null;
    data: UserStory | null;
  };
};

/**
 * get backlogs type
 */

export type getBacklogsRequestParams = {
  id: number;
  page?: number;
  search_text?: string;
  filter?: string;
};

export type getBacklogsSuccessResponse = {
  data: {
    user_stories: UserStory[];
    current_page: number;
    next_page: number;
    total_page: number;
  };
  status_code: string;
};

/**
 * add user story type
 */

export type addUserStoryRequestPayload = {
  proj_id: number;
  title: string;
  due_date?: string;
  responsible?: number;
  priority?: number;
  description?: string;
  status?: any;
  tags?: string[];
  followers?: number[];
};

export type addUserStorySucessResponse = {
  status_code: string;
  data: Omit<UserStory, 'due_date'> & {due_date: string};
};

/**
 * get user story details
 */

export type getUserStoryDetailsParams = {
  id: number;
};

export type getUserStoryDetailsSuccessResponse = {
  status_code: string;
  data: UserStory & {created_at: string; updated_at: string};
};

/**
 * update user story types
 */

export type updateUserStoryDetailsPayload = {
  proj_id: number;
  id: number;
  title: string;
  due_date?: string;
  responsible?: number;
  priority?: number;
  description?: string;
  status?: any;
  tags?: string[];
  followers?: number[];
};

export type updateUserStoryDetailsSuccessResponse = {
  status_code: string;
  data: UserStory;
};

/**
 * delete user story types
 */

export type deleteUserStoryDetailsPayload = {
  id: number;
};

/**
 * validate suer story tpe
 */
export type validateUserStoryRequestPayload = {
  id: number;
  is_validated: boolean;
};

export type validateUserStorySuccessResponse = {
  status_code: string;
  data: UserStory;
};

/**
 * values declaration
 */

const initialState: UserStoryStateType = {
  isLoading: false,
  errorUserStoryState: false,
  successUserStoryState: false,
  currentBacklogPage: 0,
  nextBacklogPage: null,
  totalBacklogPages: 0,
  backlogsList: [],
  selectedStoryData: {
    id: null,
    data: null,
  },
};

const UserStorySlice = createSlice({
  name: 'userStory',
  initialState,
  reducers: {
    getBacklogsRequest(state, action: PayloadAction<getBacklogsRequestParams>) {
      state.isLoading = true;
      state.errorUserStoryState = false;
    },
    getBacklogsFailure(state, action: PayloadAction<string | boolean>) {
      state.isLoading = false;
      state.errorUserStoryState = action.payload;
    },
    getBacklogsSuccess(
      state,
      action: PayloadAction<getBacklogsSuccessResponse>,
    ) {
      state.isLoading = false;
      state.backlogsList = action.payload.data.user_stories;
      state.currentBacklogPage = action.payload.data.current_page;
      state.nextBacklogPage = action.payload.data.next_page;
      state.totalBacklogPages = action.payload.data.total_page;
    },

    addUserStoryRequest(
      state,
      action: PayloadAction<addUserStoryRequestPayload>,
    ) {
      state.isLoading = true;
      state.errorUserStoryState = false;
    },
    addUserStoryFailure(state, action: PayloadAction<string | boolean>) {
      state.isLoading = false;
      state.errorUserStoryState = action.payload;
    },
    addUserStorySuccess(
      state,
      action: PayloadAction<addUserStorySucessResponse>,
    ) {
      state.isLoading = false;
      state.successUserStoryState = action.payload.status_code;
    },

    getUserStoryDetailsRequest(
      state,
      action: PayloadAction<getUserStoryDetailsParams>,
    ) {
      state.isLoading = true;
      state.errorUserStoryState = false;
    },
    getUserStoryDetailsSuccess(
      state,
      action: PayloadAction<getUserStoryDetailsSuccessResponse>,
    ) {
      state.isLoading = false;
      state.selectedStoryData.data = action.payload.data;
    },
    getUserStoryDetailsFailure(state, action: PayloadAction<string | boolean>) {
      state.isLoading = false;
      state.errorUserStoryState = action.payload;
    },

    updateUserStoryRequest(
      state,
      action: PayloadAction<updateUserStoryDetailsPayload>,
    ) {
      state.isLoading = true;
      state.errorUserStoryState = false;
    },
    updateUserStoryFailure(state, action: PayloadAction<string | boolean>) {
      state.isLoading = false;
      state.errorUserStoryState = action.payload;
    },
    updateUserStorySuccess(
      state,
      action: PayloadAction<updateUserStoryDetailsSuccessResponse>,
    ) {
      state.isLoading = false;
      state.successUserStoryState = action.payload.status_code;
    },

    deleteUserStoryRequest(
      state,
      action: PayloadAction<deleteUserStoryDetailsPayload>,
    ) {
      state.isLoading = true;
      state.errorUserStoryState = false;
    },
    deleteUserStoryFailure(state, action: PayloadAction<string | boolean>) {
      state.isLoading = false;
      state.errorUserStoryState = false;
    },
    deleteUserStorySuccess(
      state,
      action: PayloadAction<getBacklogsSuccessResponse>,
    ) {
      state.isLoading = false;
      state.successUserStoryState = action.payload.status_code;
      state.backlogsList = action.payload.data.user_stories;
      state.currentBacklogPage = action.payload.data.current_page;
      state.nextBacklogPage = action.payload.data.next_page;
      state.totalBacklogPages = action.payload.data.total_page;
    },

    resetMessage(state) {
      state.successUserStoryState = false;
      state.errorUserStoryState = false;
    },

    setCurrentSelectedStory(state, action: PayloadAction<number>) {
      if (!state.selectedStoryData) {
        state.selectedStoryData = {id: null, data: null};
      }
      state.selectedStoryData.id = action.payload;
    },

    validateUserStoryRequest(
      state,
      action: PayloadAction<validateUserStoryRequestPayload>,
    ) {
      state.isLoading = true;
      state.errorUserStoryState = false;
    },

    validateUserStorySuccess(
      state,
      action: PayloadAction<validateUserStorySuccessResponse>,
    ) {
      state.isLoading = false;
      state.successUserStoryState = action.payload.status_code;
      state.selectedStoryData.data = action.payload.data;
    },
    validateUserStoryFailure(state, action: PayloadAction<boolean | string>) {
      state.isLoading = false;
      state.errorUserStoryState = action.payload;
    },
  },
});

export default UserStorySlice.reducer;
export const {
  getBacklogsFailure,
  getBacklogsRequest,
  getBacklogsSuccess,
  resetMessage,
  addUserStoryFailure,
  addUserStoryRequest,
  addUserStorySuccess,
  setCurrentSelectedStory,
  getUserStoryDetailsFailure,
  getUserStoryDetailsRequest,
  getUserStoryDetailsSuccess,
  updateUserStoryFailure,
  updateUserStoryRequest,
  updateUserStorySuccess,
  deleteUserStoryFailure,
  deleteUserStoryRequest,
  deleteUserStorySuccess,
  validateUserStoryRequest,
  validateUserStorySuccess,
  validateUserStoryFailure,
} = UserStorySlice.actions;
